import { Icon } from '@iconify/react'
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Spinner,
  Dropdown,
  DropdownTrigger,
  Button,
  DropdownMenu,
  DropdownItem,
} from '@nextui-org/react'
import { IconPlus } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import toast from 'react-hot-toast'
import { useParams } from 'react-router-dom'
import { useSocketContextData } from 'src/context/socket'
import api from 'src/lib/api'
import { PowerDocsEventsName } from 'src/types/SocketEvents'

export default function AttachmentList({ doc, setDoc }) {
  const [uploading, setUpload] = useState<boolean>(false)
  const params = useParams()
  const onDrop = async (acceptedFiles: File[]) => {
    setUpload(true)
    const files = await Promise.all(
      acceptedFiles.map(async (file: File) => {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('create', 'true')
        formData.append('fileName', file.name)
        formData.append('fileType', file.name.split('.').pop()?.toUpperCase() ?? '')
        formData.append('powerDocUUID', doc?.uuid ?? params.documentUUID)
        const res = await api.post('/powerdocs/attachement', formData)
        return res.data
      }),
    )
    setDoc((prev: any) => ({
      ...prev,
      attachments: [...(files ?? []), ...(prev.attachments ?? [])],
    }))
    setUpload(false)
  }
  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    maxFiles: 10 - doc?.attachments?.length,
  })
  const { io } = useSocketContextData()
  const handleAction = (key: string, file: any) => {
    switch (key) {
      case 'download':
        window.open(file.link, '_blank')
        break
      case 'delete':
        toast.promise(
          async () => {
            await io.emitWithAck(PowerDocsEventsName.DELETE_ATTACHMENT, {
              fileId: file.id,
              token: localStorage.getItem('accessToken'),
            })
            setDoc((prev: any) => ({
              ...prev,
              attachments: prev?.attachments.filter((f: any) => f.id !== file.id),
            }))
          },
          {
            loading: `Deleting ${file.fileName}`,
            success: 'File has been removed',
          },
        )
        break
    }
  }
  return (
    <div className="pt-4">
      <input {...getInputProps()} />
      <Table>
        <TableHeader>
          <TableColumn>Document Name</TableColumn>
          <TableColumn>Upload Date</TableColumn>
          <TableColumn>Document Type</TableColumn>
          <TableColumn> </TableColumn>
        </TableHeader>
        <TableBody
          items={doc?.attachments ?? []}
          emptyContent={
            <div
              {...getRootProps({
                className: 'flex flex-col gap-2 mt-2 bg-white rounded-xl justify-center items-center',
              })}
            >
              {uploading ? (
                <div className={'flex flex-col justify-center items-center p-4'}>
                  <div className={'bg-white p-2 rounded-full mb-2'}>
                    <Spinner size="lg" />
                  </div>
                  <p className={'font-regular text-sm'}>
                    <span className={'pr-1 font-medium text-blue-400 cursor-pointer'}>Uploading file</span>
                  </p>
                </div>
              ) : (
                <div className={'flex flex-col justify-center items-center p-4'}>
                  <div className={'bg-white p-2 rounded-full mb-2'}>
                    <Icon icon={'lucide:cloud-upload'} />
                  </div>
                  <p className={'font-regular text-sm'}>
                    <span className={'pr-1 font-medium text-blue-400 cursor-pointer'}>
                      Click to upload or drag and drop
                    </span>
                    <span className={'font-light text-blue-400 '}>(max 10 files)</span>
                  </p>
                  <span className={'text-xs font-regular'}>PDF, DOCX, VIDEO, IMAGE (max. 5 MB per file)</span>
                </div>
              )}
            </div>
          }
        >
          {doc?.attachments?.map((item) => (
            <TableRow key={`attachment_${item.uuid}`} className="border-b-1 border-b-balck-500">
              <TableCell onClick={() => window.open(item.link, '_blank')} className=" cursor-pointer">
                <span className="text-sm text-black-500 font-medium underline">{item.fileName ?? '-'}</span>
              </TableCell>
              <TableCell>
                <span className="text-sm text-black-500 font-medium">{dayjs(item.createdAt).format('DD/MM/YYYY')}</span>
              </TableCell>
              <TableCell>
                <span className="text-sm text-black-500 font-medium">{item.fileType ?? '-'}</span>
              </TableCell>
              <TableCell>
                <Dropdown>
                  <DropdownTrigger>
                    <Button
                      variant="light"
                      className="focus:none data-[state=open]:bg-muted text-muted-foreground flex size-8"
                    >
                      <Icon icon={'lucide:more-vertical'} />
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu aria-label="Actions" onAction={(key: any) => handleAction(key, item)}>
                    <DropdownItem key={'download'}>
                      <span className="flex flex-row gap-2 items-center">
                        <Icon icon={'lucide:download'} color="#38A1B1" />
                        Download
                      </span>
                    </DropdownItem>
                    <DropdownItem key={'delete'}>
                      <span className="flex flex-row gap-2 items-center">
                        <Icon icon={'lucide:trash-2'} color="#38A1B1" />
                        Delete
                      </span>
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </TableCell>
            </TableRow>
          ))}
          {doc?.attachments?.length > 0 ? (
            <TableRow>
              <TableCell>
                <div className="flex items-center justify-between">
                  <div className="text-muted-foreground hidden flex-1 text-sm lg:flex">
                    <div
                      className={'flex flex-row gap-1 items-center cursor-pointer'}
                      onClick={() => {
                        open()
                      }}
                    >
                      <IconPlus size={20} color={'#3F3F46'} />
                      <span className={'text-medium text-[#3F3F46] font-medium'}>Add file</span>
                    </div>
                  </div>
                  <div className="flex w-full items-center gap-8 lg:w-fit"></div>
                </div>
              </TableCell>
              <TableCell>
                <></>
              </TableCell>
              <TableCell>
                <></>
              </TableCell>
              <TableCell>
                <></>
              </TableCell>
            </TableRow>
          ) : (
            <></>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
